import { Card, CardContent } from "./../../Components/ui/card";
import { useOverview } from "./../../hooks";
import React, { useEffect, useState, useCallback } from "react";
import Loader from "./../../Components/Loader/PageLoader";
import { BeniCountryConfig } from "./../../api";
import DynamicForm from "./DynamicForm";
import {
  filterAddressFields,
  filterFields,
} from "./../../Utils/helperFunction";

function ChangeMethod({
  formData,
  setFormData,
  errors,
  handleInputChange,
  handlefeildBlur,
  setDynamicValidation,
  setDynamicAddressValidation,
  setErrors,
  HandleSubmit,
}) {
  const {
    data,
    setData,
    selectedMethod,
    setSelectedMethod,
    formattedFields,
    setFormattedFields,
    useBeneficiaryDetails,
    beneficiarySelected,
    beniCreationError,
  } = useOverview();

  const [methods, setMethods] = useState({});
  const [loading, setLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [formattedAddressFields, setFormattedAddressFields] = useState([]);

  // Get beneficiary data
  const { data: beneficiaryData, isLoading: beneficiaryDataIsLoading } =
    useBeneficiaryDetails({
      beneficiaryId: beneficiarySelected?.beneficiaryId,
    });

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Reset all states to default when component unmounts
      setFormData({});
      setErrors({});
      setSelectedMethod(null);
      setData([]);
      setFormattedFields([]);
    };
  }, []);

  // Set initial form data from beneficiary details
  useEffect(() => {
    if (beneficiaryData?.data) {
      setFormData((prevData) => ({
        ...prevData,
        country_code: beneficiaryData.data.beniConfigCountry?.country,
        account_currency: beneficiaryData.data.beniConfigCountry?.currency,
        account_number: beneficiaryData.data.accountNo,
      }));
    }
  }, [beneficiaryData, beneficiaryDataIsLoading]);

  // Auto-select method if there's only one option
  useEffect(() => {
    if (Object.keys(methods).length === 1) {
      const onlyMethod = Object.keys(methods)[0];
      setSelectedMethod(onlyMethod);
      setFormData((prevData) => ({
        ...prevData,
        payment_method: onlyMethod,
      }));
    }
  }, [methods]);

  // Fetch available methods based on country code
  const fetchMethods = useCallback(async () => {
    if (!formData.country_code) return;

    try {
      setLoading(true);
      const result = await BeniCountryConfig({ formData });
      const methodsResponse = result?.data[0]?.methods || [];

      const filteredMethods = methodsResponse.reduce((acc, method) => {
        const methodName = method.methodName;

        // Skip the method if it matches beneficiarySelected?.method
        if (methodName === beneficiarySelected?.method) {
          return acc;
        }

        acc[methodName] = {
          title: methodName === "LOCAL" ? "Borderless Instant" : "SWIFT",
          duration:
            methodName === "LOCAL"
              ? "(0-1 Business Days)"
              : "(1-3 Business Days)",
          charges: methodName === "LOCAL" ? "5 USD" : "15 USD",
          recommended: methodName === "LOCAL",
          fields: method.fields.map((field) => ({
            fieldName: field.fieldName,
            dataType: field.dataType,
            required: field.required,
            displayName: field.displayName,
            validation: field.validation,
          })),
        };

        return acc;
      }, {});

      setMethods(filteredMethods);
    } catch (error) {
      console.error("Error fetching methods:", error);
    } finally {
      setLoading(false);
    }
  }, [formData.country_code, beneficiarySelected?.method]);

  // Fetch fields for selected payment method
  const fetchFields = useCallback(async () => {
    if (!formData.payment_method) return;

    try {
      setFormLoading(true);
      const result = await BeniCountryConfig({
        formData,
        selectedMethod: formData.payment_method,
      });
      setData(result?.data[0]?.methods[0]?.fields);
    } catch (error) {
      console.error("Error fetching fields:", error);
    } finally {
      setFormLoading(false);
    }
  }, [formData.payment_method]);

  // Handle method selection
  const handleMethodSelect = (method) => {
    setSelectedMethod(method);
    setFormData((prev) => ({
      ...prev,
      payment_method: method,
    }));
  };

  // Format field names for display
  const formatDisplayName = (name) => {
    return name
      ?.split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Process fields data
  const processFieldsData = useCallback(() => {
    if (!data || data.length === 0) return;

    const newFormattedFields = filterFields(data)?.map((field) => ({
      ...field,
      displayName: formatDisplayName(field.displayName),
    }));

    const newAddressFormattedFields = filterAddressFields(data)?.map(
      (field) => ({
        ...field,
        displayName: formatDisplayName(field.displayName),
      })
    );

    // Generate initial error object
    const initialErrorData = newFormattedFields.reduce((acc, field) => {
      acc[field.fieldName] = "";
      return acc;
    }, {});

    // Generate initial form data with default values
    const initialFormData = newFormattedFields.reduce((acc, field) => {
      if (
        field.fieldName === "account_routing_type1" ||
        field.fieldName === "account_routing_type2"
      ) {
        const cleanedString = field?.validation?.replace(/[^a-zA-Z0-9_]/g, "");
        acc[field.fieldName] = cleanedString;
      } else {
        acc[field.fieldName] = "";
      }
      return acc;
    }, {});

    // Merge with beneficiary data
    const beneficiaryInfo = beneficiaryData?.data || {};
    setFormData((prev) => ({
      ...prev,
      ...initialFormData,
      account_number: beneficiaryInfo.accountNo,
      state: beneficiaryInfo.state,
      bank_country_code: beneficiaryInfo.user?.countryCode,
      first_name: beneficiaryInfo.firstName,
      last_name: beneficiaryInfo.lastName,
      account_name: beneficiaryInfo.firstName + beneficiaryInfo.lastName,
      postcode: beneficiaryInfo.postcode,
      street_address: beneficiaryInfo.streetAddress,
      city: beneficiaryInfo.city,
      entity_type: beneficiaryInfo.entityType,
    }));

    setErrors((prev) => ({ ...prev, ...initialErrorData }));
    setFormattedFields(newFormattedFields);
    setFormattedAddressFields(newAddressFormattedFields);
  }, [data, beneficiaryData]);

  // Effect hooks for data fetching
  useEffect(() => {
    fetchMethods();
  }, [fetchMethods]);

  useEffect(() => {
    fetchFields();
  }, [fetchFields]);

  useEffect(() => {
    processFieldsData();
  }, [processFieldsData]);

  // Update validation when fields change
  useEffect(() => {
    setDynamicValidation(formattedFields);
    setDynamicAddressValidation(formattedAddressFields);
  }, [formattedFields, formattedAddressFields]);

  // Render method selection card
  const renderMethodCard = (method, methodData) => (
    <Card
      key={method}
      className={`border border-[#D3D4DA] mt-2 ${
        selectedMethod === method ? "bg-[#FFE2C7]" : "bg-white"
      }`}
      onClick={() => handleMethodSelect(method)}
    >
      <CardContent className="p-4 relative">
        <div className="flex justify-between items-center">
          <div className="space-y-2">
            <p className="font-geomanistBold text-sm text-blackShade">
              {methodData.title}
            </p>
            <p className="font-normal text-xs text-blackShade">
              {methodData.duration}
            </p>
            <p className="font-normal text-xs text-blackShade">
              Charges: {methodData.charges}
            </p>
          </div>
          <input
            type="radio"
            name="transferMethod"
            checked={selectedMethod === method}
            onChange={() => handleMethodSelect(method)}
            className="form-radio"
          />
        </div>
        {methodData.recommended && (
          <div className="absolute -top-3 right-1">
            <span className="rounded-full font-geomanistBold text-[12px] py-[5px] px-[10px] bg-blueish text-white">
              Recommended
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <form onSubmit={HandleSubmit}>
      <div className="h-[calc(100dvh-145px)] space-y-4 py-6 mb-4">
        <div>
          {loading ? (
            <div className="flex justify-center items-center mt-20">
              <Loader className="h-20 w-20" />
            </div>
          ) : Object.keys(methods).length > 0 ? (
            <>
              <p className="text-sm font-normal leading-[20px] text-greyish">
                Choose Transfer Method
              </p>
              {Object.entries(methods).map(([method, data]) =>
                renderMethodCard(method, data)
              )}
            </>
          ) : null}
        </div>

        <div>
          {formLoading ? (
            <div className="flex justify-center items-center mt-20">
              <Loader className="h-20 w-20" />
            </div>
          ) : (
            <DynamicForm
              formData={formData}
              errors={errors}
              handlefeildBlur={handlefeildBlur}
              handleInputChange={handleInputChange}
              formattedFields={formattedFields}
            />
          )}
          {beniCreationError && (
            <div className="space-y-2">
              <label className="block text-md text-red-500 font-geomanistBold mt-10">
                {beniCreationError}
              </label>
            </div>
          )}
        </div>
      </div>
    </form>
  );
}

export default ChangeMethod;
